import { useGoals } from 'features/goals';
import { useLocalizedLifeAreas } from 'features/life-areas';
import { useLifeAreaTodayReorder } from 'features/user';
import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Heart, IconFormIcons } from 'shared/assets/icons';
import { ListSectionLifeAreaHeader } from 'shared/components/ui/section-header-life-area';
import { SelfSortingTaskCardTaskSection } from 'shared/components/ui/task-card-task-section';
import { useOpenPremiumDialog } from 'shared/contexts/premium-dialog';
import { useActiveTaskId } from 'shared/contexts/task-detail';
import { useToday } from 'shared/contexts/today';
import { useUser } from 'shared/hooks/use-user';
import { ID } from 'shared/types/id';
import { DueDisplayOptions } from 'shared/types/task-base';

import { useFullCalendarDraggableTasks } from '../../hooks/use-full-calendar-draggable-tasks';
import { useLifeAreaTaskSections } from '../../hooks/use-life-area-task-sections';
import * as Styled from './life-area-grouping-tasks-list.style';

const displayDue = [DueDisplayOptions.Past, DueDisplayOptions.Future];

export const LifeAreaGroupingTasksList: React.FC = () => {
  const { t } = useTranslation();
  const today = useToday();
  const user = useUser();
  const { sections, taskCardTasks } = useLifeAreaTaskSections();
  const reorderLifeArea = useLifeAreaTodayReorder();
  const containerRef = useRef<HTMLDivElement>(null);

  const openPremiumDialog = useOpenPremiumDialog();

  const selectedTask = useActiveTaskId();

  const goals = useGoals();
  const lifeAreas = useLocalizedLifeAreas();

  const tasks = useMemo(
    () => taskCardTasks.map(({ raw }) => raw.task),
    [taskCardTasks],
  );
  useFullCalendarDraggableTasks(tasks, containerRef);

  return (
    <Styled.Container ref={containerRef}>
      {sections.map((section) => {
        const onReorder = (ids: ID[]) => reorderLifeArea(section.id, ids);

        const lifeArea = 'lifeArea' in section ? section.lifeArea : undefined;
        const name =
          lifeArea?.name ?? t('general.sections.life-areas.other.title');

        return (
          <Styled.Section key={section.id}>
            <SelfSortingTaskCardTaskSection
              goals={goals}
              lifeAreas={lifeAreas}
              lifeAreaId={lifeArea?.id}
              tagIds={user?.settings?.todayFilterByTags}
              tasks={section.items}
              selectedTask={selectedTask}
              date={today}
              displayDue={displayDue}
              customHeader={
                <ListSectionLifeAreaHeader
                  id={section.id}
                  icon={lifeArea ? IconFormIcons[lifeArea.iconName] : Heart}
                  name={name}
                  color={lifeArea?.color ?? undefined}
                  isFrozen={!!lifeArea?.frozenAt}
                  image={lifeArea?.visionPreview?.image}
                />
              }
              onReorder={onReorder}
              onPremium={openPremiumDialog}
              hideLifeArea
              headerHasImage
              canCreateTask
              canCollapse
            />
          </Styled.Section>
        );
      })}
    </Styled.Container>
  );
};
